import React, { useState } from "react"
import { lmsConnectionsApi } from "../../lib/api"

const PROVIDERS = [
  {
    id: "canvas",
    name: "Canvas",
    urlLabel: "Instance URL",
    urlPlaceholder: "https://your-school.instructure.com",
    requiresToken: true,
  },
  {
    id: "ical",
    name: "Calendar feed",
    urlLabel: "iCal Feed URL",
    urlPlaceholder: "https://your-school.blackboard.com/webapps/calendar/calendarFeed/...",
    requiresToken: false,
  },
]

const AddLmsConnectionForm = ({ onSuccess, onCancel }) => {
  const [provider, setProvider] = useState("canvas")
  const [name, setName] = useState("")
  const [instanceUrl, setInstanceUrl] = useState("")
  const [accessToken, setAccessToken] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const selectedProvider = PROVIDERS.find((p) => p.id === provider)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      const data = await lmsConnectionsApi.create({
        provider,
        name: name || selectedProvider.name,
        instance_url: instanceUrl,
        ...(selectedProvider.requiresToken ? { access_token: accessToken } : {}),
      })
      onSuccess(data.connection)
    } catch (err) {
      setError(err.message || "Failed to add connection")
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {provider === "canvas" && (
        <a
          href="/help/canvas-token"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors group"
        >
          <div className="p-2 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
            <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-indigo-900">Need help getting your Canvas token?</p>
            <p className="text-xs text-indigo-700">View our step-by-step guide with screenshots</p>
          </div>
          <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )}

      {provider === "ical" && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm font-medium text-blue-900">Use a calendar feed when direct LMS access is unavailable.</p>
          <p className="mt-1 text-xs text-blue-700">
            Calendar feeds usually include assignment names and due dates, but not grades or submission status.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            LMS Provider
          </label>
          <select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value)
              setAccessToken("")
            }}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Connection Name (optional)
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`My ${selectedProvider.name}`}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Give this connection a name to identify it (useful if you have multiple)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {selectedProvider.urlLabel}
          </label>
          <input
            type="url"
            value={instanceUrl}
            onChange={(e) => setInstanceUrl(e.target.value)}
            placeholder={selectedProvider.urlPlaceholder}
            required
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {selectedProvider.requiresToken && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Token
            </label>
            <input
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="Enter your access token"
              required
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? "Connecting..." : "Connect"}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </form>
  )
}

export default AddLmsConnectionForm
